<template>
  <div class="container-fluid">
    <section>
      <div class="py-8">
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div class="col-12 col-lg-auto mb-4 mb-lg-0">
            <div class="d-flex align-items-center">
              <h4 class="mb-0 me-2">{{ $t('sales_insights') }}</h4>
              <button 
                class="btn btn-sm btn-outline-secondary ms-2" 
                @click="showSettings = !showSettings"
                :title="$t('customize_dashboard')"
              >
                <AppIcon name="bi-gear" class="bi bi-gear" />
              </button>
            </div>
          </div>
          <div class="col-12 col-lg-3">
            <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select">
              <option value="">{{$t('filter_by_date')}}</option>
              <option value="today">{{$t('today')}}</option>
              <option value="yesterday">{{$t('yesterday')}}</option>
              <option value="this_week">{{$t('this_week')}}</option>
              <option value="last_week">{{$t('last_week')}}</option>
              <option value="this_month">{{$t('this_month')}}</option>
              <option value="custom">{{$t('custom_date')}}</option>
            </select>
          </div>
          <div class="col-12 col-lg-3" v-if="dateRangeOption === 'custom'">
            <date-picker
              :clearable="true"
              :editable="false"
              class="w-100"
              v-model="range"
              type="date"
              range
              :multi-calendars="1"
              :placeholder="$t('select_date_range')"
            ></date-picker>
          </div>
        </div>
        
        <!-- Settings Panel -->
        <div v-if="showSettings" class="card mt-3 mb-3 shadow-sm">
          <div class="card-body">
            <h6 class="card-title mb-3"><AppIcon name="bi-sliders" class="bi bi-sliders me-2" />{{$t('customize_dashboard')}}</h6>
            <div class="row">
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.salesAndProfit" id="toggleSalesProfit">
                  <label class="form-check-label small" for="toggleSalesProfit">{{$t('sales_and_profit')}}</label>
                </div>
              </div>
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.statusBreakdown" id="toggleStatus">
                  <label class="form-check-label small" for="toggleStatus">{{$t('invoice_status_breakdown')}}</label>
                </div>
              </div>
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.dailySales" id="toggleDaily">
                  <label class="form-check-label small" for="toggleDaily">{{$t('daily_sales_trend')}}</label>
                </div>
              </div>
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.topProducts" id="toggleProducts">
                  <label class="form-check-label small" for="toggleProducts">{{$t('top_products')}}</label>
                </div>
              </div>
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.topClients" id="toggleClients">
                  <label class="form-check-label small" for="toggleClients">{{$t('top_clients')}}</label>
                </div>
              </div>
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.dueInvoices" id="toggleDueList">
                  <label class="form-check-label small" for="toggleDueList">{{$t('due_invoices')}}</label>
                </div>
              </div>
            </div>
            <div class="mt-3 text-end">
                  <button 
                    class="btn btn-primary"
                    @click="saveDashboardSettings"
                  >
                    <AppIcon name="bi-save" class="bi bi-save me-1" /> {{$t('save')}}
                  </button>
                </div>
          </div>
          
        </div>
      </div>
    </section>
    
    <!-- Status Breakdown -->
    <div class="row mb-3">
      <InsightCard
        :visible="visibleCards.statusBreakdown"
        :title="$t('invoice_status_breakdown')"
        icon="pie-chart"
        container-class="col-12"
        @refresh="fetchInsights"
      >
        <div v-if="insights.status" class="row text-center">
          <div class="col-md-3 col-6 mb-2">
            <div class="p-3 border rounded">
              <AppIcon name="bi-check-circle" class="bi bi-check-circle text-success" style="font-size: 2rem;" />
              <h6 class="mt-2 mb-0">{{$t('paid')}}</h6>
              <p class="mb-0 fw-bold text-success">{{ formatMoney(insights.status.paid.amount) }}</p>
              <small class="text-muted">{{ insights.status.paid.count }} {{$t('invoices')}}</small>
            </div>
          </div>
          <div class="col-md-3 col-6 mb-2">
            <div class="p-3 border rounded">
              <AppIcon name="bi-clock" class="bi bi-clock text-warning" style="font-size: 2rem;" />
              <h6 class="mt-2 mb-0">{{$t('pending')}}</h6>
              <p class="mb-0 fw-bold text-warning">{{ formatMoney(insights.status.pending.amount) }}</p>
              <small class="text-muted">{{ insights.status.pending.count }} {{$t('invoices')}}</small>
            </div>
          </div>
          <div class="col-md-3 col-6 mb-2">
            <div class="p-3 border rounded">
              <AppIcon name="bi-hourglass-split" class="bi bi-hourglass-split text-info" style="font-size: 2rem;" />
              <h6 class="mt-2 mb-0">{{$t('partially_paid')}}</h6>
              <p class="mb-0 fw-bold text-info">{{ formatMoney(insights.status.partially_paid.amount) }}</p>
              <small class="text-muted">{{ insights.status.partially_paid.count }} {{$t('invoices')}}</small>
            </div>
          </div>
          <div class="col-md-3 col-6 mb-2">
            <div class="p-3 border rounded">
              <AppIcon name="bi-x-circle" class="bi bi-x-circle text-danger" style="font-size: 2rem;" />
              <h6 class="mt-2 mb-0">{{$t('canceled')}}</h6>
              <p class="mb-0 fw-bold text-danger">{{ formatMoney(insights.status.canceled.amount) }}</p>
              <small class="text-muted">{{ insights.status.canceled.count }} {{$t('invoices')}}</small>
            </div>
          </div>
        </div>
      </InsightCard>
    </div>
    
    <!-- Daily Sales Chart -->
    <div class="row mb-3">
      <InsightCard
        :visible="visibleCards.dailySales"
        :title="$t('daily_sales_trend')"
        icon="graph-up"
        container-class="col-12"
        @refresh="fetchInsights"
      >
        <canvas id="dailySalesChart" width="400" height="100"></canvas>
      </InsightCard>
    </div>
    
    <!-- Top Products & Top Clients -->
    <div class="row mb-3">
      <!-- Sales & Profit Card -->
      <InsightCard
        :visible="visibleCards.salesAndProfit"
        :title="$t('sales_and_profit')"
        icon="cash-stack"
        container-class="col-lg-4"
        @refresh="fetchInsights"
      >
        <div v-if="insights.summary">
          <div class="mb-3 pb-3 border-bottom">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-muted"><AppIcon name="bi-currency-dollar" class="bi bi-currency-dollar me-2" />{{$t('total_sales')}}</span>
              <h5 class="fw-bold text-success mb-0">{{ formatMoney(insights.summary.totalSales) }}</h5>
            </div>
            <small class="text-muted">{{ insights.summary.invoicesCount }} {{$t('invoices')}}</small>
          </div>
          <div>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-muted"><AppIcon name="bi-graph-up-arrow" class="bi bi-graph-up-arrow me-2" />{{$t('total_profit')}}</span>
              <h5 class="fw-bold mb-0" :class="insights.summary.totalProfit >= 0 ? 'text-success' : 'text-danger'">
                {{ formatMoney(insights.summary.totalProfit) }}
              </h5>
            </div>
            <small class="text-muted">{{$t('from_products')}}</small>
          </div>
        </div>
        <div v-else class="text-center">
          <p class="mb-0 text-muted small">{{$t('no_data')}}</p>
        </div>
      </InsightCard>
      
      <InsightCard
        :visible="visibleCards.topProducts"
        :title="$t('top_products')"
        icon="box-seam"
        container-class="col-lg-4"
        @refresh="fetchInsights"
      >
        <div v-if="insights.topProducts && insights.topProducts.length > 0">
          <ul class="list-group list-group-flush">
            <li v-for="(product, idx) in insights.topProducts.slice(0, 5)" :key="idx" 
                class="list-group-item d-flex justify-content-between align-items-center py-2">
              <div class="d-flex align-items-center">
                <span class="badge rounded-pill bg-secondary me-2" style="font-size: 0.7rem;">{{ idx + 1 }}</span>
                <span class="text-truncate" style="max-width:70%">{{ product.label }}</span>
              </div>
              <div class="text-end">
                <span class="badge rounded-pill bg-info">{{ product.quantity }} {{$t('units')}}</span>
                <div><small class="text-muted">{{ formatMoney(product.revenue) }}</small></div>
              </div>
            </li>
          </ul>
        </div>
        <div v-else class="text-center py-3">
          <p class="mb-0 text-muted small">{{$t('no_data')}}</p>
        </div>
      </InsightCard>
      
      <InsightCard
        :visible="visibleCards.topClients"
        :title="$t('top_clients')"
        icon="people-fill"
        container-class="col-lg-4"
        @refresh="fetchInsights"
      >
        <div v-if="insights.topClients && insights.topClients.length > 0">
          <ul class="list-group list-group-flush">
            <li v-for="(client, idx) in insights.topClients.slice(0, 5)" :key="idx" 
                class="list-group-item d-flex justify-content-between align-items-center py-2">
              <div class="d-flex align-items-center">
                <span class="badge rounded-pill bg-secondary me-2" style="font-size: 0.7rem;">{{ idx + 1 }}</span>
                <span class="text-truncate" style="max-width:60%">{{ client.name }}</span>
              </div>
              <div class="text-end">
                <span class="fw-bold text-success">{{ formatMoney(client.revenue) }}</span>
                <div><small class="text-muted">{{ client.invoiceCount }} {{$t('invoices')}}</small></div>
              </div>
            </li>
          </ul>
        </div>
        <div v-else class="text-center py-3">
          <p class="mb-0 text-muted small">{{$t('no_data')}}</p>
        </div>
      </InsightCard>
    </div>
    
    <!-- Due Invoices Table -->
    <div class="row">
      <InsightCard
        :visible="visibleCards.dueInvoices"
        :title="$t('due_invoices')"
        icon="file-earmark-text"
        container-class="col-12"
        @refresh="fetchInsights"
      >
        <div>
          <div class="table-responsive">
            <table class="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <sortable-table-header field="reference" label="reference" :sort-field="dueInvoiceSortField" :sort-direction="dueInvoiceSortDirection" header-class="" @sort="toggleSort($event, 'dueInvoiceSortField', 'dueInvoiceSortDirection')" />
                  <sortable-table-header field="client" label="client" :sort-field="dueInvoiceSortField" :sort-direction="dueInvoiceSortDirection" header-class="" @sort="toggleSort($event, 'dueInvoiceSortField', 'dueInvoiceSortDirection')" />
                  <sortable-table-header field="total" label="total" :sort-field="dueInvoiceSortField" :sort-direction="dueInvoiceSortDirection" header-class="text-end" @sort="toggleSort($event, 'dueInvoiceSortField', 'dueInvoiceSortDirection')" />
                  <sortable-table-header field="amount_paid" label="paid" :sort-field="dueInvoiceSortField" :sort-direction="dueInvoiceSortDirection" header-class="text-end" @sort="toggleSort($event, 'dueInvoiceSortField', 'dueInvoiceSortDirection')" />
                  <sortable-table-header field="due" label="due" :sort-field="dueInvoiceSortField" :sort-direction="dueInvoiceSortDirection" header-class="text-end" @sort="toggleSort($event, 'dueInvoiceSortField', 'dueInvoiceSortDirection')" />
                  <sortable-table-header field="status" label="status" :sort-field="dueInvoiceSortField" :sort-direction="dueInvoiceSortDirection" header-class="" @sort="toggleSort($event, 'dueInvoiceSortField', 'dueInvoiceSortDirection')" />
                  <sortable-table-header field="date" label="date" :sort-field="dueInvoiceSortField" :sort-direction="dueInvoiceSortDirection" header-class="" @sort="toggleSort($event, 'dueInvoiceSortField', 'dueInvoiceSortDirection')" />
                </tr>
              </thead>
              <tbody>
                <tr v-for="invoice in sortedDueInvoices" :key="invoice.id">
                  <td><strong>{{ invoice.reference }}</strong></td>
                  <td>{{ invoice.client }}</td>
                  <td class="text-end">{{ formatMoney(invoice.total) }}</td>
                  <td class="text-end">{{ formatMoney(invoice.amount_paid) }}</td>
                  <td class="text-end text-danger fw-bold">{{ formatMoney(invoice.due) }}</td>
                  <td>
                    <span class="badge" :class="getStatusBadgeClass(invoice.status)">
                      {{ $t(invoice.status) }}
                    </span>
                  </td>
                  <td>{{ formatDate(invoice.date) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-if="!insights.dueInvoices || !insights.dueInvoices.invoices || !insights.dueInvoices.invoices.length" class="text-center py-3">
          <p class="mb-0 text-muted small">{{$t('no_due_invoices')}}</p>
        </div>
      </InsightCard>
    </div>
  </div>
</template>

<script>
import helper from '../../helper';
import DatePicker from 'vue2-datepicker';
import moment from 'moment';
import Chart from 'chart.js';
import InsightCard from '../../components/InsightCard.vue';
import 'vue2-datepicker/index.css';
import { canPlanAccessModuleItem, getBusinessPlanKey } from '@/constants/pricingPlans'
import sortableList from '../../mixins/sortableList'

export default {
  name: 'SalesInsights',
  persistedFilterState: {
    key: 'sales-insights',
    fields: ['dateRangeOption', 'range', 'start', 'end', 'dueInvoiceSortField', 'dueInvoiceSortDirection'],
    dateFields: ['range'],
  },
  mixins: [sortableList],
  components: { DatePicker, InsightCard },
  data() {
    return {
      range: [new Date(), new Date()],
      start: moment().format('YYYY-MM-DD'),
      end: moment().format('YYYY-MM-DD'),
      dateRangeOption: '',
      showSettings: false,
      dueInvoiceSortField: '',
      dueInvoiceSortDirection: 'asc',
      
      // Insights data from backend
      insights: {
        summary: null,
        status: null,
        dueInvoices: null,
        topProducts: [],
        topClients: [],
        dailySales: []
      },
      
      // Chart instance
      dailySalesChart: null,
      
      // Visibility controls
      visibleCards: {
        salesAndProfit: true,
        statusBreakdown: true,
        dailySales: true,
        topProducts: true,
        topClients: true,
        dueInvoices: true
      }
    };
  },
  watch: {
    range() {
      if (this.range[0] == null) {
        this.start = null;
        this.end = null;
      }
      if (this.range.length && this.range[0] && this.range[1]) {
        this.start = moment(this.range[0]).format('YYYY-MM-DD');
        this.end = moment(this.range[1]).format('YYYY-MM-DD');
      } else {
        this.start = null;
        this.end = null;
      }
      this.fetchInsights();
    }
  },
  methods: {
    formatMoney(v) { 
      return helper.formatMoney(v); 
    },
    
    formatDate(d) {
      if (!d) return '';
      return moment(d).format('DD/MM/YYYY');
    },
    
    getStatusBadgeClass(status) {
      const statusMap = {
        'paid': 'bg-success',
        'pending': 'bg-warning',
        'partially_paid': 'bg-info',
        'canceled': 'bg-danger'
      };
      return statusMap[status?.toLowerCase()] || 'bg-secondary';
    },
    
    async fetchInsights() {
      try {
        let url = `${process.env.VUE_APP_API}/invoices/insights/sales`;
        if (this.start && this.end) {
          url += `?start=${this.start}&end=${this.end}`;
        }
        
        const res = await helper.customGet(url);
        this.insights = res?.data || {
          summary: null,
          status: null,
          dueInvoices: null,
          topProducts: [],
          topClients: [],
          dailySales: []
        };
        
        // Render chart after data is loaded
        this.$nextTick(() => {
          this.renderDailySalesChart();
        });
      } catch (e) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: e.message || this.$t('failed_to_fetch_data')
        });
      }
    },
    
    renderDailySalesChart() {
      this.$nextTick(() => {
        const el = document.getElementById('dailySalesChart');
        if (!el) return;
        
        if (this.dailySalesChart) {
          this.dailySalesChart.destroy();
        }
        
        if (!this.insights.dailySales || this.insights.dailySales.length === 0) {
          return;
        }
        
        const labels = this.insights.dailySales.map(x => moment(x.date).format('MMM DD'));
        const data = this.insights.dailySales.map(x => x.amount);
        
        this.dailySalesChart = new Chart(el, {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: this.$t('sales_amount'),
              data,
              borderColor: '#3498db',
              backgroundColor: 'rgba(52, 152, 219, 0.1)',
              borderWidth: 3,
              pointBackgroundColor: '#fff',
              pointBorderColor: '#3498db',
              pointBorderWidth: 2,
              pointRadius: 4,
              fill: true,
              tension: 0.3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: false },
            tooltips: {
              callbacks: {
                label: (ti, d) => `${this.$t('amount')}: ${this.formatMoney(d.datasets[0].data[ti.index])}`
              }
            },
            scales: {
              yAxes: [{
                ticks: {
                  callback: (v) => this.formatMoney(v)
                }
              }]
            }
          }
        });
      });
    },

        loadDashboardSettings() {
        const metadata = this.business?.metadata || {};
        if (metadata.sales_insights) {
          this.visibleCards = metadata.sales_insights;
        }
    },

    async saveDashboardSettings() {
      const metadata = this.business?.metadata || {};
      metadata.sales_insights = { ...this.visibleCards };
      
      // Save to backend
      await helper.customPost(`${process.env.VUE_APP_API}/businesses`, {
          metadata: metadata
      });
      this.$toast.success('Dashboard settings saved successfully.');
    },
    
    handleDateRangeChange() {
      if (this.dateRangeOption === '') {
        this.range = [];
        this.fetchInsights();
      } else if (this.dateRangeOption === 'today') {
        const start = new Date();
        const end = new Date();
        this.range = [start, end];
      } else if (this.dateRangeOption === 'yesterday') {
        const start = new Date();
        const end = new Date();
        start.setTime(start.getTime() - 3600 * 1000 * 24);
        end.setTime(end.getTime() - 3600 * 1000 * 24);
        this.range = [start, end];
      } else if (this.dateRangeOption === 'this_week') {
        const start = new Date();
        const end = new Date();
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
        this.range = [start, end];
      } else if (this.dateRangeOption === 'last_week') {
        const start = new Date();
        const end = new Date();
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 14);
        end.setTime(end.getTime() - 3600 * 1000 * 24 * 7);
        this.range = [start, end];
      } else if (this.dateRangeOption === 'this_month') {
        const start = new Date();
        const end = new Date();
        start.setDate(1);
        this.range = [start, end];
      } else if (this.dateRangeOption === 'custom') {
        this.range = [];
      }
    }
  },
  
  async mounted() {
    // Check permissions
    if (!helper.checkModuleIsEnabled('sales_insights')) return this.$router.push({ path: '/' });
    if (helper.checkAccess('view_invoices') == -1) return this.$router.push({ path: '/' });
    if (helper.checkAccess('view_invoices') == -2) return this.$router.push({ path: '/subscribe' });
    const planKey = getBusinessPlanKey(this.$store.getters.BUSINESS)
    if (!canPlanAccessModuleItem(planKey, 'sales', 'view_invoices', 'sales_insights')) return this.$router.push({ path: '/subscribe' });

    this.loadDashboardSettings();

    // Bootstrap icons if not loaded
    if (!document.getElementById('bootstrap-icons')) {
      const link = document.createElement('link');
      link.id = 'bootstrap-icons';
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css';
      document.head.appendChild(link);
    }

    // Initialize with this_month
    this.dateRangeOption = 'this_month';
    this.handleDateRangeChange();
    
    // await this.fetchInsights();
  },
  
  beforeDestroy() {
    if (this.dailySalesChart) {
      this.dailySalesChart.destroy();
    }
  },
   computed: {
    sortedDueInvoices() {
      return this.sortRows(this.insights.dueInvoices?.invoices || [], this.dueInvoiceSortField, this.dueInvoiceSortDirection);
    },
    business() {
      return this.$store.state.business;
    }
  }
};
</script>

<style scoped>
.card { 
  transition: all 0.3s ease-in-out; 
  border-radius: 12px; 
  overflow: hidden; 
}
.card:hover { 
  transform: translateY(-4px); 
  box-shadow: 0 10px 25px rgba(0,0,0,0.1) !important; 
}
#dailySalesChart { 
  height: 300px; 
  min-height: 250px; 
}
</style>
